<template>
  <multiselect v-model="selectedOptions"
               @update:modelValue="onSelectVehicles"
               :options="vehiclesList"
               :multiple="!singleSelect"
               :close-on-select="singleSelect"
               :clear-on-select="false"
               :preserve-search="true"
               :show-labels="false"
               :show-no-results="false"
               :show-no-options="false"
               :allow-empty="resolvedAllowEmpty"
               :placeholder="$t('settings.generic.select-items')"
               label="fullName" track-by="deviceId"
               class="tracking-multiselect fit-content vehicles-select"
               :class="{'vehicles-select-clearable': showClearSelection}">
    <template #caret="{ toggle }">
      <button
          v-if="showClearSelection"
          type="button"
          class="transparent vehicles-select-clear"
          @mousedown.prevent.stop
          @click.prevent.stop="clearSelection"
      >
        <i class="bi bi-x font-size-20"></i>
      </button>
      <div @mousedown.prevent.stop="toggle()" class="multiselect__select"></div>
    </template>
    <template #option="props">
      <div class="tracking-multiselect-with-checkbox">
        <input type="checkbox" v-if="!singleSelect" :checked="isVehicleChecked(props.option.deviceId)" @focus.prevent :key="props.option.deviceId" />
        <span class="option__title">{{ props.option.fullName }}</span>
      </div>
    </template>
    <template #selection="{ values, isOpen }">
      <span class="multiselect__single" v-if="values.length" v-show="!isOpen">
        {{ $t('settings.generic.selected-items') }} {{ values.length }}
      </span>
    </template>
    <template #placeholder>
      <span>
        {{ $t('settings.generic.select-placeholder') }}
      </span>
    </template>
    <template #beforeList v-if="this.vehiclesList.length > 0 && !this.singleSelect">
      <li class="multiselect__element" @click="toggleVehiclesSelection">
        <span class="multiselect__option tracking-multiselect-with-checkbox">
          <input type="checkbox" :checked="areAllVehiclesChecked()" @focus.prevent>
          <span class="option__title"><b>{{ $t('settings.generic.select-all') }}</b></span>
        </span>
      </li>
    </template>
  </multiselect>
</template>

<script>
import Multiselect from "vue-multiselect";
import {mapState} from "vuex";
import {entityDataManager} from "@/assets/js/data-manipulation";

export default {
  name: "ChooseVehicles",
  components: {Multiselect},
  props: {
    modelValue: {
      type: [Array, Object],
      default() { return []; }
    },
    preselectAll: {
      type: [Boolean, null],
      default: true
    },
    useDeviceName: {
      type: [Boolean, null],
      default: false
    },
    singleSelect: {
      type: [Boolean, null],
      default: false
    },
    showGroupName: {
      type: Boolean,
      default: false
    },
    addAllOption: {
      type: [Boolean],
      default: false
    },
    allowEmpty: {
      type: [Boolean, null],
      default: null
    },
    vehiclesFilterCallback: {
      type: [Function, null]
    }
  },
  data() {
    return {
      selectedOptions: this.modelValue,
      groupIdFilter: null
    };
  },
  computed: {
    ...mapState(['vehicles', 'devices', 'groups']),
    vehiclesList() {
      let vehicles = this.vehicles;
      if (this.vehiclesFilterCallback) {
        vehicles = vehicles.filter(this.vehiclesFilterCallback);
      }

      let mappedVehicles = vehicles
          .map(vehicle => {

            let device = null;
            let group = null;

            let name = vehicle.fullName;

            if (this.useDeviceName) {
              device = entityDataManager.getDeviceByVehicle(this.devices, vehicle);
              name = device.name;
            }

            if (this.showGroupName) {
              if (!device) {
                device = entityDataManager.getDeviceByVehicle(this.devices, vehicle);
              }

              group = entityDataManager.getById(this.groups, device.groupId);
              if (group) {
                name += ` (${group.name})`;
              }
            }

            return {
              id: vehicle.id,
              deviceId: vehicle.deviceId,
              fullName: name,
              groupId: group?.id,
            };
          });

      if (this.groupIdFilter !== null) {
        mappedVehicles = mappedVehicles.filter(x => x.groupId === this.groupIdFilter || !x.groupId);
      }

      mappedVehicles.sort((a, b) => a.fullName.localeCompare(b.fullName));

      if (this.addAllOption) {
        mappedVehicles.unshift({id: null, deviceId: null, fullName: this.$t('generic.all'), groupId: null});
      }

      return mappedVehicles;
    },
    resolvedAllowEmpty() {
      return this.allowEmpty ?? !this.singleSelect;
    },
    showClearSelection() {
      return this.singleSelect && this.resolvedAllowEmpty && !!this.selectedOptions?.id;
    }
  },
  mounted() {
    if (this.preselectAll) {
      this.selectAllVehicles();
    }
    else if (this.singleSelect && (this.selectedOptions || this.addAllOption)) {
      if (this.selectedOptions?.id) {
        const selectedVehicle = entityDataManager.getById(this.vehiclesList, this.selectedOptions?.id ?? null);
        this.onSelectVehicles(selectedVehicle);
      }
      else if (this.selectedOptions?.deviceId) {
        const selectedVehicle = entityDataManager.getVehicleByDeviceId(this.vehiclesList, this.selectedOptions?.deviceId ?? null);
        this.onSelectVehicles(selectedVehicle);
      }
      else if (this.addAllOption) {
        const selectedVehicle = entityDataManager.getById(this.vehiclesList, null);
        this.onSelectVehicles(selectedVehicle, false);
      }
    }
  },
  watch: {
    modelValue(value) {
      if (this.singleSelect) {
        if (value?.deviceId) {
          const selectedOptions = entityDataManager.getVehicleByDeviceId(this.vehiclesList, value?.deviceId ?? null);
          this.onSelectVehicles(selectedOptions, false);
        }
        else {
          const selectedOptions = entityDataManager.getById(this.vehiclesList, value?.id ?? null);
          this.onSelectVehicles(selectedOptions, false);
        }
        return;
      }
      this.selectedOptions = value;
    },
    vehicles(_, oldValue) {
      if (oldValue?.length === 0 && this.preselectAll) {
        this.selectAllVehicles();
      }
    }
  },
  methods: {
    isVehicleChecked(key) {
      return this.selectedOptions?.some(x => x.deviceId === key);
    },
    areAllVehiclesChecked() {
      return this.selectedOptions?.length === this.vehiclesList.length;
    },
    toggleVehiclesSelection() {
      if (this.vehiclesList.length <= 0) {
        return;
      }

      this.onSelectVehicles(this.areAllVehiclesChecked() ? [] : this.vehiclesList);
    },
    selectAllVehicles() {
      this.onSelectVehicles(this.vehiclesList);
    },
    onSelectVehicles(data, triggerValueUpdate = true) {
      this.selectedOptions = data;
      if (triggerValueUpdate) {
        const data = !this.singleSelect ? this.selectedOptions :
            !this.selectedOptions?.id ? null : this.selectedOptions;
        // in case all option is selected and id is null - pass null to the parent
        this.$emit('update:modelValue', data);
      }
    },
    clearSelection() {
      this.onSelectVehicles(null);
    },
    filterByGroupId(groupId) {
      if (this.selectedOptions !== null) {
        if (this.singleSelect) {
          if (this.selectedOptions.groupId !== groupId) {
            this.selectedOptions = null;
          }
        }
      }

      this.groupIdFilter = groupId;
    },
    clearGroupIdFilter() {
      this.groupIdFilter = null;
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/scss/variables.scss';

.vehicles-select {
  width: 240px;
  height: 37px;
}

.vehicles-select-clearable {
  :deep(.multiselect__tags) {
    padding-right: 68px;
  }
}

.vehicles-select-clear {
  position: absolute;
  top: 2px;
  right: 26px;
  z-index: 2;
  width: 30px;
  height: 38px;
  color: #999;
  line-height: 1;

  &:hover {
    color: $tracking-accent;
  }
}

// Mobile:
@media (max-width: $breaking-point) {
  .vehicles-select {
    width: 100%;
  }
}
</style>